import React from 'react';
import { StyleSheet, Dimensions, View, Text, Animated } from 'react-native';
import { connect } from 'react-redux';
import i18n from 'i18n-js';

import Item from './components/item';
import Header from './components/header';
import Loading from '@components/loading';
import Refresh from '@components/common/Refresh';

import { getData, loadNewData } from '@reducers/productionOrders';

import { moderateScale, isPhone } from '@helpers';
import { GridBox, scale, width } from '@components/frame';

import ColorWrapper from '@styles/colors';
import useStatusBar from '@components/common/useStatusBar';

import FeatureFlags from '@components/featureFlags';
import AnimatedHeader from '@components/common/AnimatedHeader';

import useManageProductionOrders from './hook/useManageProductionOrders';

const Main = ({ getData, loadNewData, productionOrders = {}, navigation, C }) => {
  useStatusBar({ C });

  const {
    data,
    itemSize,
    boxStyle,
    fadeAnim,
    scrollRef,
    scrollListener,
    fetchData,
    totalRecords,
    isError,
    isLoaded,
    hasData,
  } = useManageProductionOrders({ getData, loadNewData, productionOrders, navigation });

  if (isError) {
    return <Refresh onPress={fetchData} />;
  }

  if (!isLoaded) {
    return <Loading />;
  }

  const startFrom = 50;

  return (
    <GridBox
      key="mainBox"
      {...{
        backgroundColor: C('Background / Light / System'),
        headerOffset: true,
        sliderOffset: false,
        customWidth: Dimensions.get('window').width,
      }}
    >
      <View style={{ width: '100%', height: '100%', overflowY: 'scroll', WebkitOverflowScrolling: 'touch' }}>
        <AnimatedHeader
          {...{
            fadeAnim,
            C,
            text: i18n.t('Production Orders'),
            scrollRef,
            startFrom: startFrom,
            back: true,
          }}
        />
        <View style={{ width: '100%', height: '100%', justifyContent: 'center', alignContent: 'center', alignItems: 'center' }}>
          <Animated.ScrollView
            key="list"
            ref={scrollRef}
            style={{ width: '100%', flex: 1, backgroundColor: C('Background / Light / System') }}
            contentContainerStyle={{ width: '100%', justifyContent: 'center', alignContent: 'center', alignItems: 'center' }}
            scrollEventThrottle={1}
            onScroll={Animated.event([{ nativeEvent: { contentOffset: { y: fadeAnim } } }], {
              useNativeDriver: true,
              listener: scrollListener,
            })}
          >
            <View style={{ width, height: '100%' }}>
              <Header
                {...{
                  text: i18n.t('Production Orders'),
                  length: totalRecords,
                }}
                key="header"
              />
              <FeatureFlags {...{ name: 'general-showapiresponsetime', C }} key="request-time">
                <View style={{ position: 'absolute', zIndex: 1000000, top: scale(25), left: scale(20) }}>
                  <Text style={{ color: C('Label/Light/Quinary'), fontSize: 12, marginBottom: 10 }}>
                    Request - ({productionOrders.requestTimeEndMS} ms) |{' '}
                    {productionOrders && productionOrders.refreshInfo && productionOrders.refreshInfo.start
                      ? `Refresh token - ${productionOrders.refreshInfo.start} - ${productionOrders.refreshInfo.start}`
                      : 'Token is valid'}
                  </Text>
                </View>
              </FeatureFlags>

              {!hasData ? (
                <View
                  style={{
                    width: '100%',
                    minHeight: '100%',
                    justifyContent: 'center',
                    alignContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <Text style={{ color: C('Label / Light / Primary') }}>{i18n.t('No data')}</Text>
                </View>
              ) : (
                <View style={[styles.itemWrapper, boxStyle]}>
                  {data.map((item, index) => (
                    <Item
                      key={'Item' + index}
                      item={item}
                      {...{
                        index,
                        boxStyle,
                        itemSize,
                      }}
                      isEmpty={item === 'empty'}
                    />
                  ))}
                </View>
              )}
            </View>
          </Animated.ScrollView>
        </View>
      </View>
    </GridBox>
  );
};

const styles = StyleSheet.create({
  itemContainer: {
    flex: 1,
  },
  itemWrapper: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    alignContent: 'center',
    marginTop: moderateScale(isPhone ? -4 : -5),
  },
});

const mapStateToProps = ({ productionOrders }) => ({ productionOrders });

export default connect(mapStateToProps, { getData, loadNewData })(ColorWrapper(Main));
